import { useCallback, useEffect, useMemo, type ReactNode } from "react";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

import { ErrorState } from "@/components/app/error-state";
import { RouteFallback } from "@/components/app/loading-state";
import { TenantSuspendedScreen } from "@/components/app/tenant-suspended";
import { api, toApiError } from "@/lib/api";
import { qk } from "@/lib/query-keys";
import { useAppStore } from "@/stores/app-store";
import type { ApiError, BranchSummary, SessionResponse, SessionUser } from "@/types/api";

/**
 * Session state.
 *
 * The current user is server state, so it lives in the query cache like
 * everything else (contract §7.7) — there is no parallel auth store to drift
 * out of sync with it.
 */

async function fetchSession(): Promise<SessionResponse> {
  const { data } = await api.get<SessionResponse>("auth/session/");
  return data;
}

export interface SessionState {
  session: SessionResponse | null;
  user: SessionUser | null;
  tenant: SessionResponse["tenant"] | null;
  branches: BranchSummary[];
  permissions: string[];
  /**
   * The wire field name, kept verbatim: the API is snake_case in both directions
   * and there is no renaming layer [D-11]. `defaultBranchId` is the same value
   * under the name React code tends to reach for first.
   */
  default_branch_id: number | null;
  defaultBranchId: number | null;
  is_impersonated: boolean;
  isImpersonated: boolean;
  impersonator: SessionResponse["impersonator"];
  isAuthenticated: boolean;
  isLoading: boolean;
  isError: boolean;
  error: ApiError | null;
  refetch: () => void;
}

const NO_BRANCHES: BranchSummary[] = [];
const NO_PERMISSIONS: string[] = [];

/**
 * The bootstrap call. Every screen reads it; TanStack Query dedupes it to one
 * request.
 */
export function useSession(): SessionState {
  const query = useQuery({
    queryKey: qk.session(),
    queryFn: fetchSession,
    retry: false,
    staleTime: 60_000,
  });

  const error = query.error ? toApiError(query.error) : null;
  const data = query.data ?? null;

  return {
    session: data,
    user: data?.user ?? null,
    tenant: data?.tenant ?? null,
    branches: data?.branches ?? NO_BRANCHES,
    permissions: data?.permissions ?? NO_PERMISSIONS,
    default_branch_id: data?.default_branch_id ?? null,
    defaultBranchId: data?.default_branch_id ?? null,
    is_impersonated: data?.is_impersonated ?? false,
    isImpersonated: data?.is_impersonated ?? false,
    impersonator: data?.impersonator ?? null,
    isAuthenticated: data !== null,
    isLoading: query.isLoading,
    isError: query.isError,
    error,
    refetch: () => void query.refetch(),
  };
}

/** True when `permissions` grants `code`. UI-hiding only — never authorisation. */
export function hasPermission(permissions: readonly string[], code?: string): boolean {
  if (!code) return true;
  return permissions.includes(code);
}

export function useHasPermission(code?: string): boolean {
  const { permissions } = useSession();
  return hasPermission(permissions, code);
}

/** True when the caller holds every one of `codes`. */
export function useHasEveryPermission(codes: readonly string[]): boolean {
  const { permissions } = useSession();
  return codes.every((c) => permissions.includes(c));
}

// ---------------------------------------------------------------------------
// Login / logout
// ---------------------------------------------------------------------------

export interface LoginCredentials {
  /**
   * Phone number, email address or member code. The wire field is still called
   * `phone` because that is what the API calls it and what every saved password
   * manager entry already holds; a phone number is simply no longer the only
   * thing it may contain ([D-02], reversed 2026-09-06).
   */
  phone: string;
  password: string;
  /** Set on the second attempt, after an ambiguous phone offered a choice. */
  user_id?: number;
}

/** One "did you mean?" option behind a shared phone number. */
export interface LoginChoice {
  user_id: number;
  full_name: string;
  member_code: string;
}

/**
 * The number reaches more than one account.
 *
 * A husband and wife share a mobile; the server refuses to guess which of them
 * is signing in and answers 409 with the candidates. This is thrown rather than
 * returned so `mutateAsync` rejects like any other failure and the caller cannot
 * mistake it for a session.
 */
export class AmbiguousIdentifierError extends Error {
  readonly code = "ambiguous_identifier";
  readonly status = 409;
  readonly choices: LoginChoice[];

  constructor(detail: string, choices: LoginChoice[]) {
    super(detail);
    this.name = "AmbiguousIdentifierError";
    this.choices = choices;
  }
}

function readChoices(error: unknown): LoginChoice[] {
  if (!axios.isAxiosError(error)) return [];
  const data = error.response?.data as { choices?: unknown } | undefined;
  return Array.isArray(data?.choices) ? (data.choices as LoginChoice[]) : [];
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation<SessionResponse, ApiError | AmbiguousIdentifierError, LoginCredentials>({
    mutationFn: async (credentials) => {
      try {
        const { data } = await api.post<SessionResponse>("auth/login/", credentials);
        return data;
      } catch (error) {
        const apiError = toApiError(error);
        if (apiError.status === 409 && apiError.code === "ambiguous_identifier") {
          throw new AmbiguousIdentifierError(apiError.detail, readChoices(error));
        }
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(qk.session(), data);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const setActiveBranchId = useAppStore((s) => s.setActiveBranchId);

  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      await api.post("auth/logout/");
    },
    onSettled: () => {
      // Clear regardless of the server's answer: the user asked to leave, and a
      // stale cache after a failed logout is worse than a redundant redirect.
      setActiveBranchId(null);
      queryClient.clear();
      window.location.assign("/login");
    },
  });
}

// ---------------------------------------------------------------------------
// Route guard
// ---------------------------------------------------------------------------

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Gate for every authenticated surface.
 *
 * Order matters: a suspended tenant must render the block screen even for a
 * signed-in user, and an unauthenticated user must never see it.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { isLoading, isError, error, session, refetch } = useSession();
  const tenantSuspended = useAppStore((s) => s.tenantSuspended);
  const suspensionReason = useAppStore((s) => s.suspensionReason);

  if (isLoading) return <RouteFallback />;

  if (isError && error?.status === 401) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (isError && error?.status !== 402) {
    return (
      <div className="mx-auto flex min-h-svh max-w-md items-center justify-center p-6">
        <ErrorState
          title={t("error.networkTitle")}
          description={error?.detail ?? t("error.networkBody")}
          onRetry={refetch}
        />
      </div>
    );
  }

  const suspended =
    tenantSuspended ||
    error?.status === 402 ||
    session?.tenant.status === "SUSPENDED" ||
    session?.tenant.status === "ARCHIVED";

  if (suspended) {
    return (
      <TenantSuspendedScreen
        reason={suspensionReason || (session?.tenant.suspension_reason ?? "")}
      />
    );
  }

  if (!session) return <RouteFallback />;

  return <>{children}</>;
}

/**
 * Keeps the selected branch honest: clears a stored id the user no longer has
 * access to, and falls back to their default branch on first sign-in.
 *
 * The fallback fires in exactly two cases — never chosen, or chose a branch that
 * has since gone away. It must NOT fire on a deliberate `null`, which is the
 * owner-level "All branches" rollup: this effect re-runs on the store change,
 * so restoring the default there put the branch straight back and made the
 * cross-branch view unselectable. `branchInitialised` is what tells the two
 * meanings of `null` apart.
 */
export function useSyncActiveBranch(): void {
  const { branches, defaultBranchId, isAuthenticated } = useSession();
  const activeBranchId = useAppStore((s) => s.activeBranchId);
  const branchInitialised = useAppStore((s) => s.branchInitialised);
  const setActiveBranchId = useAppStore((s) => s.setActiveBranchId);

  useEffect(() => {
    if (!isAuthenticated || branches.length === 0) return;

    // A deliberate "All branches" is a valid, final state.
    if (branchInitialised && activeBranchId === null) return;
    // A still-valid branch selection is left alone.
    if (activeBranchId !== null && branches.some((b) => b.id === activeBranchId)) return;

    const fallback =
      defaultBranchId && branches.some((b) => b.id === defaultBranchId)
        ? defaultBranchId
        : (branches.find((b) => b.is_default)?.id ?? branches[0]?.id ?? null);

    if (fallback !== null) setActiveBranchId(fallback);
  }, [
    activeBranchId,
    branchInitialised,
    branches,
    defaultBranchId,
    isAuthenticated,
    setActiveBranchId,
  ]);
}

/** The branch object the UI is currently pointed at, or null for "all". */
export function useActiveBranch(): BranchSummary | null {
  const { branches } = useSession();
  const activeBranchId = useAppStore((s) => s.activeBranchId);
  return useMemo(
    () => branches.find((b) => b.id === activeBranchId) ?? null,
    [branches, activeBranchId],
  );
}

/** Toast helper shared by the mutation hooks. */
export function useApiErrorToast() {
  const { t } = useTranslation();
  return useCallback(
    (error: unknown, fallbackKey = "error.unexpected") => {
      const apiError = toApiError(error);
      toast.error(apiError.detail || t(fallbackKey));
    },
    [t],
  );
}
